import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import { Calendar, CalendarOff, DollarSign, Download, Layers, Package, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AdminTable, type AdminTableColumn, type SortState } from "@/components/ui/admin-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MobileFilterField, MobileFilters } from "@/components/ui/mobile-filters";
import { PageHeader } from "@/components/ui/page-header";
import { ResizableSheet } from "@/components/ui/resizable-sheet";
import { inventoryApi, type SnapshotRow } from "@/features/inventory/inventory-api";
import { PharmacySelector } from "@/features/reports/pharmacy-selector";
import { useTenantSwitcher } from "@/features/tenant-switcher";
import { Visible } from "@/features/visibility/visible";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { exportToExcel } from "@/lib/export-excel";
import { ensureElementVisible } from "@/lib/route-guards";
import { useDebounce } from "@/lib/use-debounce";

// TODO(stock-available, v133): ten widok korzysta z `inventory_daily_snapshot`
// ktory NIE ma jeszcze kolumny `total_stock_available`. Snapshot batches
// (inventory_snapshot_batches) maja wstrz/ilows od v134 — mozna agregowac
// available on-the-fly, albo dodac kolumne do daily_snapshot. Po wdrozeniu:
// rozszerzyc SnapshotRow + endpoint + dodac kolumne 'Stan dostepny' z toggle
// jak na /kartoteka/towary.

export const Route = createFileRoute("/_auth/kartoteka/stan-magazynu")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.kartoteka.stan-magazynu",
			location.pathname,
		);
	},
	component: StanMagazynu,
});

const PAGE_SIZE = 100;

const formatNumber = (n: number) => n.toLocaleString("pl-PL", { maximumFractionDigits: 0 });
const formatCurrency = (n: number) =>
	n.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const columns: AdminTableColumn<SnapshotRow>[] = [
	{
		key: "productName",
		header: "Produkt",
		width: "250px",
		sortable: true,
		render: (row) => (
			<div>
				<div className="font-medium text-xs">{row.productName ?? "\u2014"}</div>
				<div className="text-[10px] text-muted-foreground font-mono">{row.bloz07}</div>
			</div>
		),
	},
	{
		key: "nrtow",
		header: "Nr towaru",
		width: "90px",
		className: "text-right",
		sortable: true,
		// Numer towaru to identyfikator, nie miara — na telefonie tylko w szczegolach.
		mobileRole: "detail",
		render: (row) => (
			<span className="font-mono tabular-nums text-xs">{row.nrtow ?? "\u2014"}</span>
		),
	},
	{
		key: "form",
		header: "Postac",
		width: "140px",
		render: (row) => {
			const parts = [row.form, row.dosage].filter(Boolean);
			return (
				<span className="text-muted-foreground text-xs">{parts.join(" \u2022 ") || "\u2014"}</span>
			);
		},
	},
	{
		key: "totalStock",
		header: "Stan",
		width: "90px",
		className: "text-right",
		sortable: true,
		render: (row) => {
			const display =
				row.totalStock < 1
					? row.totalStock.toLocaleString("pl-PL", { maximumFractionDigits: 2 })
					: formatNumber(row.totalStock);
			return <span className="font-mono tabular-nums text-xs">{display}</span>;
		},
	},
	{
		key: "valuePurchaseNet",
		header: "Wartosc netto",
		width: "120px",
		className: "text-right",
		sortable: true,
		render: (row) => (
			<span className="font-mono tabular-nums text-xs">{formatCurrency(row.valuePurchaseNet)}</span>
		),
	},
	{
		key: "valuePurchaseGross",
		header: "Wartosc brutto",
		width: "120px",
		className: "text-right",
		sortable: true,
		render: (row) => (
			<span className="font-mono tabular-nums text-xs">
				{formatCurrency(row.valuePurchaseGross)}
			</span>
		),
	},
	{
		key: "medianSellPrice",
		header: "Cena sprzedazy",
		width: "120px",
		className: "text-right",
		sortable: true,
		render: (row) =>
			row.medianSellPrice != null && row.medianSellPrice > 0 ? (
				<span className="font-mono tabular-nums text-xs">
					{formatCurrency(row.medianSellPrice)} zl
				</span>
			) : (
				<span className="text-muted-foreground text-xs">{"\u2014"}</span>
			),
	},
	{
		key: "batchCount",
		header: "Partii",
		width: "70px",
		className: "text-right",
		sortable: true,
		render: (row) => <span className="font-mono tabular-nums text-xs">{row.batchCount}</span>,
	},
	{
		key: "earliestExpiry",
		header: "Min. data waznosci",
		width: "130px",
		sortable: true,
		render: (row) => (
			<span className="text-xs text-muted-foreground">{row.earliestExpiry ?? "\u2014"}</span>
		),
	},
];

function StanMagazynu() {
	const { selectedTenantId } = useTenantSwitcher();
	const [idFirm, setIdFirm] = useState<number | null>(null);
	// Nazwa wybranej apteki — tylko do etykiety chipa na telefonie.
	const [pharmacyName, setPharmacyName] = useState<string | null>(null);
	const isBelowMd = useIsBelowMd();
	const [date, setDate] = useState<string>(format(subDays(new Date(), 1), "yyyy-MM-dd"));
	const [search, setSearch] = useState("");
	const debouncedSearch = useDebounce(search, 300);
	const [sort, setSort] = useState<SortState | null>({ column: "totalStock", dir: "desc" });
	const [expandedBloz, setExpandedBloz] = useState<string | null>(null);
	const [isExporting, setIsExporting] = useState(false);
	// Per [[feedback-ui-state-persistence]]: filtry musza przezyc F5 — lazy
	// useState czyta z localStorage + useEffect sync zapisuje przy zmianie.
	const [hideExpired, setHideExpired] = useState<boolean>(() => {
		try {
			return localStorage.getItem("stanMagazynu.hideExpired") === "true";
		} catch {
			return false;
		}
	});
	useEffect(() => {
		try {
			localStorage.setItem("stanMagazynu.hideExpired", String(hideExpired));
		} catch {
			// Ignorowane — quota exceeded lub private mode.
		}
	}, [hideExpired]);

	const { data: batchData, isFetching: batchLoading } = useQuery({
		queryKey: ["inventory", "batches", selectedTenantId, idFirm, expandedBloz, date],
		queryFn: () =>
			inventoryApi.getBatches({
				idFirm: idFirm as number,
				bloz07: expandedBloz as string,
				date,
			}),
		enabled: !!idFirm && !!expandedBloz && !!date,
		staleTime: 1000 * 60 * 5,
	});

	const {
		data: snapshotData,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		queryKey: [
			"inventory",
			"snapshot",
			selectedTenantId,
			idFirm,
			date,
			sort,
			debouncedSearch,
			hideExpired,
		],
		queryFn: ({ pageParam }) =>
			inventoryApi.getSnapshot({
				idFirm: idFirm as number,
				date,
				limit: PAGE_SIZE,
				offset: pageParam as number,
				sortBy: sort?.column,
				sortDir: sort?.dir,
				q: debouncedSearch || undefined,
				hideExpired: hideExpired || undefined,
			}),
		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) => {
			const items = lastPage.items ?? [];
			const total = lastPage.total ?? 0;
			const loaded = allPages.reduce((sum, p) => sum + (p.items ?? []).length, 0);
			if (items.length < PAGE_SIZE || total === 0) return undefined;
			return loaded < total ? loaded : undefined;
		},
		enabled: !!idFirm && !!date,
		staleTime: 1000 * 60 * 5,
		placeholderData: keepPreviousData,
	});

	const snapshotRows = useMemo(
		() => snapshotData?.pages.flatMap((p) => p.items ?? []) ?? [],
		[snapshotData],
	);
	const snapshotTotal = snapshotData?.pages[0]?.total;

	const { data: availableDates } = useQuery({
		queryKey: ["inventory", "snapshot-dates", selectedTenantId, idFirm],
		queryFn: () => inventoryApi.getAvailableDates(idFirm ?? undefined),
		enabled: !!idFirm,
		staleTime: 1000 * 60 * 10,
	});

	const availableDateSet = useMemo(() => new Set(availableDates ?? []), [availableDates]);

	const { data: summaryData } = useQuery({
		queryKey: ["inventory", "snapshot-summary", selectedTenantId, idFirm, date, hideExpired],
		queryFn: () =>
			inventoryApi.getSnapshotSummary({
				date,
				idFirm: idFirm ?? undefined,
				hideExpired: hideExpired || undefined,
			}),
		enabled: !!date && !!idFirm,
		staleTime: 1000 * 60 * 5,
	});

	const summary = useMemo(() => {
		if (!summaryData || summaryData.length === 0) {
			return { totalProducts: 0, totalStock: 0, totalValueNet: 0, totalValueGross: 0 };
		}
		return summaryData.reduce(
			(acc, row) => ({
				totalProducts: acc.totalProducts + row.totalProducts,
				totalStock: acc.totalStock + row.totalStock,
				totalValueNet: acc.totalValueNet + row.totalValueNet,
				totalValueGross: acc.totalValueGross + row.totalValueGross,
			}),
			{ totalProducts: 0, totalStock: 0, totalValueNet: 0, totalValueGross: 0 },
		);
	}, [summaryData]);

	async function handleExport() {
		if (!idFirm) return;
		setIsExporting(true);
		try {
			const data = await inventoryApi.getExport({
				idFirm,
				date,
				hideExpired: hideExpired || undefined,
			});

			if (data.truncated) {
				toast.warning(
					`Plik niekompletny: zwrocono ${data.items.length}/${data.totalUnfilteredItems} produktow ` +
						`i ${data.batches.length}/${data.totalUnfilteredBatches} partii. ` +
						`Apteka przekracza limit eksportu (200 000 wierszy). Zglos to administratorowi.`,
				);
			}

			const slug = (data.pharmacyName ?? `apteka_${idFirm}`)
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, "-")
				.replace(/^-|-$/g, "");

			const summaryRow = data.items.reduce(
				(acc, r) => ({
					totalStock: acc.totalStock + (r.totalStock ?? 0),
					valuePurchaseNet: acc.valuePurchaseNet + (r.valuePurchaseNet ?? 0),
					valuePurchaseGross: acc.valuePurchaseGross + (r.valuePurchaseGross ?? 0),
				}),
				{ totalStock: 0, valuePurchaseNet: 0, valuePurchaseGross: 0 },
			);

			await exportToExcel({
				fileName: `stan-magazynu_${slug}_${date}.xlsx`,
				sheets: [
					{
						name: "Stan",
						columns: [
							{ id: "nrtow", header: "Nr towaru", widthPx: 70, isNumeric: true },
							{ id: "bloz07", header: "BLOZ07", widthPx: 80, isNumeric: false },
							{ id: "productName", header: "Nazwa", widthPx: 240, isNumeric: false },
							{ id: "form", header: "Postac", widthPx: 100, isNumeric: false },
							{ id: "dosage", header: "Dawka", widthPx: 80, isNumeric: false },
							{ id: "totalStock", header: "Stan", widthPx: 70, isNumeric: true },
							{ id: "valuePurchaseNet", header: "Wartosc netto", widthPx: 110, isNumeric: true },
							{
								id: "valuePurchaseGross",
								header: "Wartosc brutto",
								widthPx: 110,
								isNumeric: true,
							},
							{
								id: "medianSellPrice",
								header: "Cena sprzedazy",
								widthPx: 110,
								isNumeric: true,
							},
							{ id: "batchCount", header: "Partii", widthPx: 60, isNumeric: true },
							{
								id: "earliestExpiry",
								header: "Min. data waznosci",
								widthPx: 120,
								isNumeric: false,
							},
						],
						rows: data.items as unknown as Record<string, unknown>[],
						summaryRow,
						summaryLabel: "SUMA",
						summaryLabelColumnId: "productName",
					},
					{
						name: "Partie",
						columns: [
							{ id: "nrtow", header: "Nr towaru", widthPx: 70, isNumeric: true },
							{ id: "bloz07", header: "BLOZ07", widthPx: 80, isNumeric: false },
							{ id: "productName", header: "Nazwa", widthPx: 240, isNumeric: false },
							{ id: "seria", header: "Seria", widthPx: 100, isNumeric: false },
							{ id: "expiryDate", header: "Data waznosci", widthPx: 100, isNumeric: false },
							{ id: "purchaseDate", header: "Data zakupu", widthPx: 100, isNumeric: false },
							{ id: "quantity", header: "Ilosc", widthPx: 70, isNumeric: true },
							{
								id: "purchasePriceNet",
								header: "Cena zakupu netto",
								widthPx: 120,
								isNumeric: true,
							},
							{
								id: "batchValueNet",
								header: "Wartosc partii netto",
								widthPx: 130,
								isNumeric: true,
							},
						],
						rows: data.batches as unknown as Record<string, unknown>[],
					},
				],
			});
		} catch (e) {
			toast.error(
				`Nie udalo sie wyeksportowac stanu magazynu: ${e instanceof Error ? e.message : "blad serwera"}`,
			);
		} finally {
			setIsExporting(false);
		}
	}

	// Telefon: apteka, data i „ukryj przeterminowane” w chipach i arkuszu filtrow,
	// wyszukiwarka nad lista, eksport jako ikona w rzedzie chipow.
	const mobileDateInput = (
		<Input
			type="date"
			className="h-10 w-full text-sm disabled:cursor-not-allowed"
			value={date}
			disabled={!idFirm}
			max={availableDates?.[0] ?? undefined}
			min={availableDates?.[availableDates.length - 1] ?? undefined}
			onChange={(e) => {
				const val = e.target.value;
				if (availableDateSet.size > 0 && !availableDateSet.has(val)) return;
				setDate(val);
			}}
		/>
	);
	const mobileHideExpired = (
		<label className="flex min-h-10 items-center gap-2 text-sm cursor-pointer select-none">
			<input
				type="checkbox"
				className="size-5 cursor-pointer disabled:cursor-not-allowed"
				checked={hideExpired}
				disabled={!idFirm}
				onChange={(e) => setHideExpired(e.target.checked)}
			/>
			<span>Ukryj przeterminowane</span>
		</label>
	);
	const mobilePharmacy = (
		<PharmacySelector
			value={idFirm}
			onChange={(val, name) => {
				setIdFirm(val);
				setPharmacyName(name);
			}}
		/>
	);

	return (
		<Visible id="page.kartoteka.stan-magazynu" label="Stan Magazynu">
			<div className="flex flex-col h-full overflow-hidden">
				<div className="flex flex-col gap-4 p-4 pb-0 max-md:gap-2 max-md:p-0">
					<PageHeader
						title="Stan Magazynu"
						description="Dzienny snapshot stanu magazynowego aptek"
					/>

					{isBelowMd && (
						<div className="px-3 pt-2">
							<div className="relative">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
								<Input
									type="text"
									placeholder="Szukaj produktu..."
									className="h-10 pl-9 text-sm disabled:cursor-not-allowed"
									value={search}
									disabled={!idFirm}
									onChange={(e) => setSearch(e.target.value)}
								/>
							</div>
						</div>
					)}
					<MobileFilters
						chips={[
							{
								id: "pharmacy",
								label: "Apteka",
								value: idFirm ? (pharmacyName ?? `Apteka ${idFirm}`) : "Wybierz aptekę",
								active: !!idFirm,
								picker: mobilePharmacy,
							},
							{
								id: "date",
								label: "Data snapshotu",
								value:
									idFirm && availableDateSet.size > 0 && !availableDateSet.has(date)
										? `${date} · brak danych`
										: date,
								picker: mobileDateInput,
							},
							{
								id: "hideExpired",
								label: "Przeterminowane",
								value: hideExpired ? "Bez przeterminowanych" : "Z przeterminowanymi",
								active: hideExpired,
								picker: mobileHideExpired,
							},
						]}
						sheetContent={
							<>
								<MobileFilterField label="Apteka">{mobilePharmacy}</MobileFilterField>
								<MobileFilterField label="Data snapshotu">{mobileDateInput}</MobileFilterField>
								{mobileHideExpired}
							</>
						}
						onClear={() => setHideExpired(false)}
						resultLabel={
							snapshotTotal != null
								? `Pokaż ${snapshotTotal.toLocaleString("pl-PL")} produktów`
								: "Pokaż produkty"
						}
						actions={
							<button
								type="button"
								onClick={handleExport}
								disabled={!idFirm || isExporting}
								aria-label={isExporting ? "Generowanie eksportu" : "Eksport XLSX"}
								title={
									!idFirm ? "Najpierw wybierz aptekę" : `Eksport pelnego stanu magazynu na ${date}`
								}
								className="inline-flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground disabled:opacity-50"
							>
								<Download className="size-4" />
							</button>
						}
					>
						<div className="flex items-center gap-2 flex-wrap">
							<PharmacySelector value={idFirm} onChange={(val) => setIdFirm(val)} />
							<div
								className={`flex items-center gap-1 bg-card border rounded-lg px-2 ${
									!idFirm ? "opacity-50 cursor-not-allowed" : ""
								}`}
								title={!idFirm ? "Najpierw wybierz aptekę" : undefined}
							>
								<Calendar className="size-3.5 text-muted-foreground" />
								<Input
									type="date"
									className="w-36 h-8 border-0 bg-transparent text-sm focus-visible:ring-0 disabled:cursor-not-allowed"
									value={date}
									disabled={!idFirm}
									max={availableDates?.[0] ?? undefined}
									min={availableDates?.[availableDates.length - 1] ?? undefined}
									onChange={(e) => {
										const val = e.target.value;
										if (availableDateSet.size > 0 && !availableDateSet.has(val)) return;
										setDate(val);
									}}
								/>
								{idFirm && availableDateSet.size > 0 && !availableDateSet.has(date) && (
									<span className="text-[10px] text-status-warning-text whitespace-nowrap">
										Brak danych
									</span>
								)}
							</div>
							<div
								className={`flex items-center gap-1 bg-card border rounded-lg px-2 ${
									!idFirm ? "opacity-50 cursor-not-allowed" : ""
								}`}
								title={!idFirm ? "Najpierw wybierz aptekę" : undefined}
							>
								<Search className="size-3.5 text-muted-foreground" />
								<Input
									type="text"
									placeholder="Szukaj produktu..."
									className="w-48 h-8 border-0 bg-transparent text-sm focus-visible:ring-0 disabled:cursor-not-allowed"
									value={search}
									disabled={!idFirm}
									onChange={(e) => setSearch(e.target.value)}
								/>
							</div>
							<label
								className={`flex items-center gap-1.5 bg-card border rounded-lg px-2 h-8 text-sm cursor-pointer select-none ${
									!idFirm ? "opacity-50 cursor-not-allowed" : ""
								}`}
								title={
									!idFirm
										? "Najpierw wybierz aptekę"
										: "Ukryj pozycje, ktorych najwczesniejsza partia jest juz po dacie waznosci na wybrany snapshot. Filtr stosowany takze przy eksporcie XLSX."
								}
							>
								<CalendarOff className="size-3.5 text-muted-foreground" />
								<input
									type="checkbox"
									className="size-3.5 cursor-pointer disabled:cursor-not-allowed"
									checked={hideExpired}
									disabled={!idFirm}
									onChange={(e) => setHideExpired(e.target.checked)}
								/>
								<span className="text-muted-foreground">Ukryj przeterminowane</span>
							</label>
							<Button
								onClick={handleExport}
								disabled={!idFirm || isExporting}
								variant="outline"
								size="sm"
								className="h-8 ml-auto"
								title={
									!idFirm ? "Najpierw wybierz aptekę" : `Eksport pelnego stanu magazynu na ${date}`
								}
							>
								<Download className="size-3.5 mr-1" />
								{isExporting ? "Generowanie..." : "Eksport XLSX"}
							</Button>
						</div>
					</MobileFilters>

					{/* Telefon: zamiast czterech kart KPI zwarte podsumowanie 2×2. */}
					{idFirm && isBelowMd && (
						<dl
							data-testid="stan-magazynu-mobile-summary"
							className="grid grid-cols-2 gap-x-3 gap-y-1.5 border-b border-border px-3 pb-2 text-xs"
						>
							{[
								{ label: "Produktow", value: formatNumber(summary.totalProducts) },
								{ label: "Laczny stan", value: formatNumber(summary.totalStock) },
								{ label: "Wartosc netto", value: `${formatCurrency(summary.totalValueNet)} zl` },
								{
									label: "Wartosc brutto",
									value: `${formatCurrency(summary.totalValueGross)} zl`,
								},
							].map((s) => (
								<div key={s.label} className="min-w-0">
									<dt className="text-muted-foreground">{s.label}</dt>
									<dd className="truncate font-mono text-sm font-semibold tabular-nums">
										{s.value}
									</dd>
								</div>
							))}
						</dl>
					)}
					{idFirm && !isBelowMd && (
						<div className="grid grid-cols-4 gap-3">
							<div className="bg-card border rounded-lg">
								<div className="pb-1 pt-3 px-3">
									<div className="text-xs font-medium text-muted-foreground flex items-center gap-1">
										<Package className="size-3" />
										Produktow
									</div>
								</div>
								<div className="px-3 pb-3">
									<p className="text-xl font-bold font-mono tabular-nums">
										{formatNumber(summary.totalProducts)}
									</p>
								</div>
							</div>
							<div className="bg-card border rounded-lg">
								<div className="pb-1 pt-3 px-3">
									<div className="text-xs font-medium text-muted-foreground flex items-center gap-1">
										<Layers className="size-3" />
										Laczny stan
									</div>
								</div>
								<div className="px-3 pb-3">
									<p className="text-xl font-bold font-mono tabular-nums">
										{formatNumber(summary.totalStock)}
									</p>
								</div>
							</div>
							<div className="bg-card border rounded-lg">
								<div className="pb-1 pt-3 px-3">
									<div className="text-xs font-medium text-muted-foreground flex items-center gap-1">
										<DollarSign className="size-3" />
										Wartosc netto
									</div>
								</div>
								<div className="px-3 pb-3">
									<p className="text-xl font-bold font-mono tabular-nums">
										{formatCurrency(summary.totalValueNet)} zl
									</p>
								</div>
							</div>
							<div className="bg-card border rounded-lg">
								<div className="pb-1 pt-3 px-3">
									<div className="text-xs font-medium text-muted-foreground flex items-center gap-1">
										<DollarSign className="size-3" />
										Wartosc brutto
									</div>
								</div>
								<div className="px-3 pb-3">
									<p className="text-xl font-bold font-mono tabular-nums">
										{formatCurrency(summary.totalValueGross)} zl
									</p>
								</div>
							</div>
						</div>
					)}
				</div>
				<div className="flex-1 min-h-0 p-4 max-md:p-0">
					<AdminTable
						tableId="kartoteka.stan-magazynu"
						columns={columns}
						data={snapshotRows}
						keyField="bloz07"
						emptyMessage="Brak danych dla wybranej daty"
						sort={sort}
						onSortChange={setSort}
						totalCount={snapshotTotal}
						onFetchMore={fetchNextPage}
						hasNextPage={hasNextPage}
						isFetchingNextPage={isFetchingNextPage}
						className="h-full"
						onRowClick={(row) =>
							setExpandedBloz((prev) => (prev === row.bloz07 ? null : row.bloz07))
						}
					/>
				</div>
			</div>
			<ResizableSheet
				open={!!expandedBloz}
				onOpenChange={(open) => !open && setExpandedBloz(null)}
				title={
					<div>
						<div>Partie produktu</div>
						<div className="text-xs text-muted-foreground font-normal mt-0.5">
							{snapshotRows.find((r) => r.bloz07 === expandedBloz)?.productName ?? expandedBloz}
						</div>
					</div>
				}
				defaultWidth={640}
				minWidth={480}
			>
				{batchLoading ? (
					<div className="text-xs text-muted-foreground py-4 px-4">Ladowanie partii...</div>
				) : batchData && batchData.length > 0 ? (
					<div className="px-4 pb-4">
						{/* Summary */}
						<div className="grid grid-cols-3 gap-3 mb-4 max-md:grid-cols-1 max-md:gap-2">
							<div className="bg-muted/30 rounded-lg p-2">
								<div className="text-[10px] text-muted-foreground">Partii</div>
								<div className="text-sm font-bold font-mono tabular-nums">{batchData.length}</div>
							</div>
							<div className="bg-muted/30 rounded-lg p-2">
								<div className="text-[10px] text-muted-foreground">Laczna ilosc</div>
								<div className="text-sm font-bold font-mono tabular-nums">
									{batchData
										.reduce((s, b) => s + b.quantity, 0)
										.toLocaleString("pl-PL", { maximumFractionDigits: 0 })}
								</div>
							</div>
							<div className="bg-muted/30 rounded-lg p-2">
								<div className="text-[10px] text-muted-foreground">Wartosc netto</div>
								<div className="text-sm font-bold font-mono tabular-nums">
									{batchData
										.reduce((s, b) => s + b.batchValueNet, 0)
										.toLocaleString("pl-PL", {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}{" "}
									zl
								</div>
							</div>
						</div>
						{/* Batch table — na telefonie przewija sie w bok we wlasnym kontenerze. */}
						<div className="max-md:-mx-4 max-md:overflow-x-auto max-md:px-4">
							<table className="w-full text-xs max-md:whitespace-nowrap">
								<thead>
									<tr className="border-b border-border">
										<th className="text-left py-1.5 px-2 font-semibold text-muted-foreground">
											Seria
										</th>
										<th className="text-left py-1.5 px-2 font-semibold text-muted-foreground">
											Termin waznosci
										</th>
										<th className="text-right py-1.5 px-2 font-semibold text-muted-foreground">
											Ilosc
										</th>
										<th className="text-right py-1.5 px-2 font-semibold text-muted-foreground">
											Cena zakupu
										</th>
										<th className="text-right py-1.5 px-2 font-semibold text-muted-foreground">
											Wartosc
										</th>
										<th className="text-left py-1.5 px-2 font-semibold text-muted-foreground">
											Data zakupu
										</th>
									</tr>
								</thead>
								<tbody>
									{batchData.map((b, i) => (
										<tr
											// biome-ignore lint/suspicious/noArrayIndexKey: partie tylko do odczytu; seria moze sie powtorzyc.
											key={`${b.seria}-${i}`}
											className="border-b border-border/50 last:border-0 hover:bg-muted/20"
										>
											<td className="py-1.5 px-2 font-mono">{b.seria ?? "\u2014"}</td>
											<td className="py-1.5 px-2">{b.expiryDate ?? "\u2014"}</td>
											<td className="py-1.5 px-2 text-right font-mono tabular-nums">
												{b.quantity < 1
													? b.quantity.toLocaleString("pl-PL", { maximumFractionDigits: 2 })
													: b.quantity.toLocaleString("pl-PL", { maximumFractionDigits: 0 })}
											</td>
											<td className="py-1.5 px-2 text-right font-mono tabular-nums">
												{formatCurrency(b.purchasePriceNet)} zl
											</td>
											<td className="py-1.5 px-2 text-right font-mono tabular-nums font-medium">
												{formatCurrency(b.batchValueNet)} zl
											</td>
											<td className="py-1.5 px-2">{b.purchaseDate ?? "\u2014"}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				) : (
					<div className="text-xs text-muted-foreground py-4 px-4">Brak aktywnych partii</div>
				)}
			</ResizableSheet>
		</Visible>
	);
}
